import { useState, useEffect } from 'react';
import {
  useDeleteChatHistory,
  useDeleteSearchHistory,
  useRequestExport,
  useExportStatus,
  useDownloadExport,
  type ExportStatus,
} from '../../hooks/useAccountManagement';
import { DeleteChatHistoryModal } from './DeleteChatHistoryModal';
import { DeleteSearchHistoryModal } from './DeleteSearchHistoryModal';
import { Download, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';

function ExportStatusBadge({ status }: { status: ExportStatus }) {
  switch (status) {
    case 'queued':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 text-blue-400 rounded-lg text-sm">
          <Clock className="w-3.5 h-3.5" />
          Queued
        </span>
      );
    case 'processing':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-500/10 text-yellow-400 rounded-lg text-sm">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Processing
        </span>
      );
    case 'ready':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 text-green-400 rounded-lg text-sm">
          <CheckCircle className="w-3.5 h-3.5" />
          Ready
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-500/10 text-red-400 rounded-lg text-sm">
          <XCircle className="w-3.5 h-3.5" />
          Failed
        </span>
      );
    default:
      return null;
  }
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

export function DataAccountSection() {
  const deleteChatHistory = useDeleteChatHistory();
  const deleteSearchHistory = useDeleteSearchHistory();
  const requestExport = useRequestExport();
  const exportStatus = useExportStatus();
  const { downloadExport } = useDownloadExport();

  const [showDeleteChatModal, setShowDeleteChatModal] = useState(false);
  const [showDeleteSearchModal, setShowDeleteSearchModal] = useState(false);
  const [pollingTooLong, setPollingTooLong] = useState(false);

  const currentExport = exportStatus.data?.current;
  const isExportActive = currentExport?.status === 'queued' || currentExport?.status === 'processing';
  const isExportReady = currentExport?.status === 'ready';
  const isExportFailed = currentExport?.status === 'failed';

  useEffect(() => {
    if (!isExportActive) {
      setPollingTooLong(false);
      return;
    }

    const timer = setTimeout(() => {
      setPollingTooLong(true);
    }, 5 * 60 * 1000);

    return () => clearTimeout(timer);
  }, [isExportActive, currentExport?.id]);

  const expiresAt = currentExport?.expires_at ? new Date(currentExport.expires_at) : null;
  const isExpired = expiresAt && expiresAt.getTime() < Date.now();

  return (
    <>
      <div className="bg-[#1A1F26] rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">YOUR DATA</h2>
        <div className="space-y-4">
          {/* Export Data */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white font-medium">Export My Data</div>
                <div className="text-sm text-slate-400">Download a copy of all your Filmple data (GDPR)</div>
              </div>
              {!currentExport || (!isExportActive && !isExportReady) ? (
                <button
                  onClick={() => requestExport.mutate()}
                  disabled={requestExport.isPending}
                  className="px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                >
                  {requestExport.isPending ? 'Requesting...' : 'Request Export'}
                </button>
              ) : null}
            </div>

            {currentExport && (
              <div className="mt-4 p-4 bg-[#0D1117] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <ExportStatusBadge status={currentExport.status} />
                  <span className="text-xs text-slate-500">
                    Requested {formatRelativeTime(currentExport.requested_at)}
                  </span>
                </div>

                {isExportActive && (
                  <div className="text-sm text-slate-400">
                    {pollingTooLong ? (
                      <span className="text-yellow-400">
                        Taking longer than expected. Please check back later or refresh the page.
                      </span>
                    ) : (
                      <span>Your export is being prepared. This usually takes a few minutes...</span>
                    )}
                  </div>
                )}

                {isExportReady && !isExpired && (
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-400">
                      Your export is ready.
                      {expiresAt && (
                        <span className="text-slate-500 ml-1">
                          Expires in {Math.ceil((expiresAt.getTime() - Date.now()) / 86400000)} days
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => downloadExport(currentExport.id)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 rounded-lg transition-colors font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                )}

                {isExportReady && isExpired && (
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-red-400">
                      This export has expired. Request a new one.
                    </div>
                    <button
                      onClick={() => requestExport.mutate()}
                      disabled={requestExport.isPending}
                      className="px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                    >
                      {requestExport.isPending ? 'Requesting...' : 'Request New Export'}
                    </button>
                  </div>
                )}

                {isExportFailed && (
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-red-400">
                      Export failed{currentExport.error ? `: ${currentExport.error}` : '. Please try again.'}
                    </div>
                    <button
                      onClick={() => requestExport.mutate()}
                      disabled={requestExport.isPending}
                      className="px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                    >
                      {requestExport.isPending ? 'Requesting...' : 'Retry'}
                    </button>
                  </div>
                )}

                {currentExport.download_count > 0 && (
                  <div className="text-xs text-slate-500 mt-2">
                    Downloaded {currentExport.download_count} time{currentExport.download_count === 1 ? '' : 's'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Clear Search History */}
          <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
            <div>
              <div className="text-white font-medium">Clear Search History</div>
              <div className="text-sm text-slate-400">Remove your saved top-bar search queries</div>
            </div>
            <button
              onClick={() => setShowDeleteSearchModal(true)}
              disabled={deleteSearchHistory.isPending}
              className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              Clear
            </button>
          </div>

          {/* Delete Chat History */}
          <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
            <div>
              <div className="text-white font-medium">Delete Chat History</div>
              <div className="text-sm text-slate-400">Remove all your conversations with Filmply</div>
            </div>
            <button
              onClick={() => setShowDeleteChatModal(true)}
              disabled={deleteChatHistory.isPending}
              className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {showDeleteChatModal && (
        <DeleteChatHistoryModal
          isOpen={showDeleteChatModal}
          onClose={() => setShowDeleteChatModal(false)}
          onConfirm={() => {
            deleteChatHistory.mutate();
            setShowDeleteChatModal(false);
          }}
          isDeleting={deleteChatHistory.isPending}
        />
      )}

      {showDeleteSearchModal && (
        <DeleteSearchHistoryModal
          isOpen={showDeleteSearchModal}
          onClose={() => setShowDeleteSearchModal(false)}
          onConfirm={() => {
            deleteSearchHistory.mutate();
            setShowDeleteSearchModal(false);
          }}
          isDeleting={deleteSearchHistory.isPending}
        />
      )}
    </>
  );
}
